import { OptionModel } from '@/shared/lib/types'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import clsx from 'clsx'
import { CheckIcon, ChevronDownIcon } from 'lucide-react'

type ControlledSelectProps = {
  value: string | number | null | undefined
  options: OptionModel[]
  onChange: (value: string | number | null | undefined) => void
  className?: string
  label?: string
  error?: string
  disabled?: boolean
  variant?: 'primary' | 'secondary'
  placeholder?: string
}

// Компонент для рендера иконки опции
const OptionIcon = ({ option }: { option: OptionModel }) => {
  if (option.icon) return <span className="flex-shrink-0 w-[18px] h-[18px]">{option.icon}</span>

  return null
}

export const ControlledSelect = ({
  value,
  options,
  onChange,
  className,
  variant = 'secondary',
  label,
  disabled,
  placeholder = 'Изменить',
  error,
}: ControlledSelectProps) => {
  const selectedOption = options.find((opt) => opt.value === value)

  const variantBtnClasses =
    variant === 'primary'
      ? 'bg-white dark:bg-card-background text-primary dark:text-primary-dark'
      : 'bg-white dark:bg-dark-bg text-text-secondary placeholder-text-secondary ring-card-border dark:ring-card-border-dark'

  const variantOptionsClasses =
    variant === 'primary'
      ? 'bg-white dark:bg-card-background ring-card-border dark:ring-card-border-dark'
      : 'bg-white dark:bg-dark-bg ring-card-border dark:ring-card-border-dark'

  const variantOptionClasses =
    variant === 'primary' ? 'text-text-main dark:text-white' : 'text-text-secondary '

  return (
    <div className={`w-full flex flex-col gap-2 relative ${className && className}`}>
      {label && <label className="text-text-main dark:text-white">{label}</label>}

      <Listbox
        value={selectedOption}
        onChange={(opt: OptionModel) => onChange(opt?.value ?? null)}
        disabled={disabled}
      >
        <div>
          <ListboxButton
            className={clsx(
              'block relative disabled:opacity-75 ring-card-border dark:cart-card-border-dark ring-1 ring-inset w-full text-sm/6 text-left disabled:cursor-not-allowed',
              'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-card-border',
              'rounded-lg px-3 py-2',
              variantBtnClasses,
              error && 'ring-error dark:ring-error ring-1'
            )}
          >
            <div className="flex items-center gap-3">
              {selectedOption && <OptionIcon option={selectedOption} />}
              <span>{selectedOption ? selectedOption.label : placeholder}</span>
            </div>

            <ChevronDownIcon
              className="group top-[10px] right-2 absolute w-5 h-5 pointer-events-none"
              aria-hidden="true"
            />
          </ListboxButton>
          {error && <div className="text-error text-xs text-right">{error}</div>}
        </div>

        <ListboxOptions
          anchor="bottom"
          transition
          className={clsx(
            'p-1 rounded-lg focus:outline-none ring-1 ring-inset w-[var(--button-width)] [--anchor-gap:var(--spacing-1)]',
            'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0',
            variantOptionsClasses
          )}
        >
          {options.map((option) => (
            <ListboxOption
              key={option.value}
              value={option}
              className={clsx(
                'group flex items-center gap-2 data-[focus]:bg-light-purple dark:data-[focus]:bg-dark-purple px-3 py-1.5 rounded-lg cursor-default select-none'
              )}
            >
              <CheckIcon className="invisible group-data-[selected]:visible size-4" />
              <div className="flex items-center gap-3">
                <OptionIcon option={option} />
                <div
                  className={clsx(
                    'text-sm/6',
                    variantOptionClasses,
                    'group-data-[selected]:text-black'
                  )}
                >
                  {option.label}
                </div>
              </div>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </div>
  )
}
